import React from "react";
import { Search, X } from "lucide-react";
import { Product, SelectedProduct } from "../../OfflineOrder/types";
import medicineIcon from "../../../../../../assets/images/medicineIcon.png";
import defaultProduct from "../../../../../../assets/images/default_product.jpg";

interface AddMedicineModalProps {
  showModal: boolean;
  setShowModal: (val: boolean) => void;
  searchText: string;
  handleModalSearch: (val: string) => void;
  loading: boolean;
  availableProducts: Product[];
  selectedProducts: SelectedProduct[];
  pagination: any;
  fetchProducts: (term: string, page: number, limit: number) => void;
  handleAddProduct: (product: Product) => void;
  handleRemoveProduct: (compositeId: string) => void;
  getDaysRemaining: (date: string) => number | null;
}

const AddMedicineModal: React.FC<AddMedicineModalProps> = ({
  showModal,
  setShowModal,
  searchText,
  handleModalSearch,
  loading,
  availableProducts,
  selectedProducts,
  pagination,
  fetchProducts,
  handleAddProduct,
  handleRemoveProduct,
  getDaysRemaining,
}) => {
  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => setShowModal(false)}
      />

      {/* Modal Content */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden animate-in">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-800">Add Medicines</h2>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name, batch, manufacturer..."
                value={searchText}
                onChange={(e) => handleModalSearch(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-[300px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <button
              onClick={() => setShowModal(false)}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Modal Table Header */}
        <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr] gap-2 px-6 py-3 bg-gray-50 text-xs font-semibold text-gray-500 border-b border-gray-200">
          <div>Name</div>
          <div>Batch</div>
          <div>Expiry</div>
          <div>Qty/Pack</div>
          <div>MRP</div>
          <div>MRP/Unit</div>
          <div className="text-center">Strips</div>
          <div className="text-center">Tablets</div>
          <div className="text-center">Action</div>
        </div>

        {/* Modal Table Body */}
        <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <div className="flex flex-col items-center gap-2">
                <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm">Loading medicines...</span>
              </div>
            </div>
          ) : availableProducts.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
              No products found
            </div>
          ) : (
            availableProducts.map((product, index) => {
              const daysLeft = getDaysRemaining(product.expiry_date);
              const isAdded = selectedProducts.some(
                (p) => p.compositeId === product.compositeId
              );

              return (
                <div
                  key={product.compositeId}
                  className={`grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr] gap-2 items-center px-6 py-3 border-b border-gray-100 hover:bg-green-50/30 transition-colors ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                  }`}
                >
                  {/* Name */}
                  <div className="flex items-center gap-3 pr-2 min-w-0">
                    <img
                      src={product.product_image || defaultProduct}
                      alt={product.product_name}
                      className="w-10 h-10 rounded object-cover flex-shrink-0"
                    />
                    <div className="min-w-0">
                      <div className="text-[13px] font-medium text-gray-800 truncate" title={product.product_name}>
                        {product.product_name}
                      </div>
                      <div className="text-[11px] text-gray-400 truncate" title={product.manufacturer_name}>
                        {product.manufacturer_name}
                      </div>
                    </div>
                  </div>

                  {/* Batch */}
                  <div className="text-[12px] text-gray-600 truncate" title={product.batch_number}>
                    {product.batch_number}
                  </div>

                  {/* Expiry */}
                  <div>
                    <span
                      className={`text-[12px] font-medium ${
                        daysLeft !== null && daysLeft < 30
                          ? "text-red-500"
                          : "text-gray-600"
                      }`}
                    >
                      {daysLeft !== null
                        ? daysLeft > 0
                          ? `${daysLeft} days`
                          : "Expired"
                        : "N/A"}
                    </span>
                  </div>

                  {/* Qty/Pack */}
                  <div className="text-[12px] text-gray-600">
                    {product.noOfItemsInPack}
                  </div>

                  {/* MRP */}
                  <div className="text-[12px] text-gray-700 font-medium">
                    ₹ {product.mrp?.toFixed(2)}
                  </div>

                  {/* MRP/Unit */}
                  <div className="text-[12px] text-gray-700">
                    ₹ {product.perPackPrice?.toFixed(1)}
                  </div>

                  {/* Strips */}
                  <div className="text-[12px] text-center text-gray-600">
                    {Math.floor(product.totalStock / product.noOfItemsInPack)}
                  </div>

                  {/* Tablets */}
                  <div className="text-[12px] text-center text-gray-600">
                    {product.totalStock % product.noOfItemsInPack}
                  </div>

                  {/* Action */}
                  <div className="flex justify-center">
                    <button
                      onClick={() => isAdded ? handleRemoveProduct(product.compositeId) : handleAddProduct(product)}
                      className="px-4 py-1.5 rounded-md text-white text-[12px] font-semibold transition-all hover:shadow-md active:scale-95"
                      style={{
                        backgroundColor: isAdded ? "#DC2626" : "#199C4D", // Red for remove, Green for add
                        opacity: 1
                      }}
                    >
                      {isAdded ? "Remove" : "Add"}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Modal Pagination */}
        {/* <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50">
          <span className="text-xs text-gray-500">
            Page {pagination.currentPage} of {pagination.totalPages || 1}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => {
                const newPage = pagination.currentPage - 1;
                if (newPage >= 1) fetchProducts(searchText, newPage, pagination.itemsPerPage);
              }}
              disabled={pagination.currentPage === 1}
              className="px-3 py-1.5 text-xs rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => {
                const newPage = pagination.currentPage + 1;
                if (newPage <= (pagination.totalPages || 1))
                  fetchProducts(searchText, newPage, pagination.itemsPerPage);
              }}
              disabled={pagination.currentPage === (pagination.totalPages || 1)}
              className="px-3 py-1.5 text-xs rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default AddMedicineModal;
